import Cookies from 'js-cookie';
import { GENERAL_DOMAIN, HOTLINE_TEXT } from 'sysconfig';

/**
 * Gắn khách đã có tài khoản cho cộng tác viên (CTV) qua link QR (BV-2399).
 *
 * Link QR của CTV là /affiliate-partner/register?referCode=CTV…. Khách chưa đăng nhập thì mã nằm ở
 * cookie này (domain chung, nên web và web-mobile cùng đọc được) cho tới khi khách đăng nhập — rồi
 * useAffiliateLinkClaim gọi API gắn và xoá cookie.
 */
export const AFFILIATE_LINK_COOKIE = 'affiliate_link_code';

/** Mã chờ gắn sống 3 ngày: đủ cho CS mở lại tài khoản bị khoá, không đủ lâu để gắn nhầm người sau. */
export const AFFILIATE_LINK_COOKIE_MAX_AGE = 3 * 24 * 60 * 60;

/** Khớp enum.CustomerAffiliateLinkStatus bên customer-service. */
export const AFFILIATE_LINK_STATUS = {
  LINKED: 'LINKED',
  ALREADY_LINKED: 'ALREADY_LINKED',
  REGISTERED: 'REGISTERED',
  OTHER_PARTNER: 'OTHER_PARTNER',
  INVALID: 'INVALID',
};

/** Header Set-Cookie (SSR) lưu mã chờ gắn. */
export function affiliateLinkCookieHeader(referCode) {
  const domain = GENERAL_DOMAIN ? `; Domain=${GENERAL_DOMAIN}` : '';
  return `${AFFILIATE_LINK_COOKIE}=${encodeURIComponent(referCode)}; Path=/${domain}; SameSite=Lax; Max-Age=${AFFILIATE_LINK_COOKIE_MAX_AGE}`;
}

export function getPendingAffiliateLinkCode() {
  return Cookies.get(AFFILIATE_LINK_COOKIE) || '';
}

export function clearPendingAffiliateLinkCode() {
  Cookies.remove(AFFILIATE_LINK_COOKIE, { path: '/', domain: GENERAL_DOMAIN || undefined });
  Cookies.remove(AFFILIATE_LINK_COOKIE, { path: '/' });
}

/**
 * Kết quả của API gắn: { status } khi xong (kể cả "đã thuộc CTV khác", "mã sai"), null khi lỗi hệ
 * thống / mạng — khi đó giữ mã để thử lại.
 */
export function readAffiliateLinkResult(res) {
  if (res?.status !== 'OK') return null;
  const result = res?.data?.[0];
  return result?.status ? result : null;
}

/**
 * Kết quả gắn ở server (trang /affiliate-partner/register) chuyển sang client qua cookie ngắn hạn
 * này thay vì query string — trong đó có tên CTV, không để lên URL. Client đọc một lần rồi xoá.
 */
export const AFFILIATE_LINK_RESULT_COOKIE = 'affiliate_link_result';
const AFFILIATE_LINK_RESULT_MAX_AGE = 5 * 60;

/** Header Set-Cookie (SSR) chứa kết quả gắn: { status, affiliatePartnerCode, affiliatePartnerName }. */
export function affiliateLinkResultCookieHeader({ status, affiliatePartnerCode, affiliatePartnerName } = {}) {
  const value = encodeURIComponent(JSON.stringify({ status, affiliatePartnerCode, affiliatePartnerName }));
  const domain = GENERAL_DOMAIN ? `; Domain=${GENERAL_DOMAIN}` : '';
  return `${AFFILIATE_LINK_RESULT_COOKIE}=${value}; Path=/${domain}; SameSite=Lax; Max-Age=${AFFILIATE_LINK_RESULT_MAX_AGE}`;
}

/** Đọc rồi xoá kết quả gắn do server để lại; không có / hỏng thì null. */
export function takeAffiliateLinkResult() {
  const raw = Cookies.get(AFFILIATE_LINK_RESULT_COOKIE);
  if (!raw) return null;
  Cookies.remove(AFFILIATE_LINK_RESULT_COOKIE, { path: '/', domain: GENERAL_DOMAIN || undefined });
  Cookies.remove(AFFILIATE_LINK_RESULT_COOKIE, { path: '/' });
  try {
    const result = JSON.parse(raw);
    return result?.status ? result : null;
  } catch (e) {
    return null;
  }
}

/**
 * Popup báo kết quả cho khách: { warning, title, content } hoặc null khi không cần báo. Gọi CTV bằng
 * tên (không có tên thì mã).
 *  - LINKED: vừa liên kết lần đầu với CTV này;
 *  - ALREADY_LINKED: mở lại link của chính CTV đó — báo là đã liên kết rồi;
 *  - OTHER_PARTNER: đã thuộc CTV khác (tên là CTV đang giữ khách), giữ CTV cũ — muốn chuyển thì liên hệ CSKH;
 *  - REGISTERED, INVALID: không báo.
 */
export function affiliateLinkPopupOf({ status, affiliatePartnerCode, affiliatePartnerName } = {}) {
  const partner = affiliatePartnerName || affiliatePartnerCode;
  const partnerText = partner ? `cộng tác viên ${partner}` : 'cộng tác viên';
  if (status === AFFILIATE_LINK_STATUS.LINKED) {
    return {
      warning: false,
      title: 'Liên kết thành công',
      content: `Tài khoản của bạn đã được liên kết với ${partnerText}.`,
    };
  }
  if (status === AFFILIATE_LINK_STATUS.ALREADY_LINKED) {
    return {
      warning: false,
      title: 'Đã liên kết',
      content: `Tài khoản của bạn đã được liên kết với ${partnerText} trước đó.`,
    };
  }
  if (status === AFFILIATE_LINK_STATUS.OTHER_PARTNER) {
    return {
      warning: true,
      title: 'Tài khoản đã có cộng tác viên',
      content: `Tài khoản của bạn đã được liên kết với ${partner ? partnerText : 'một cộng tác viên khác'}. Vui lòng liên hệ CSKH (${HOTLINE_TEXT}) nếu cần chuyển cộng tác viên.`,
    };
  }
  return null;
}
